import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { apiRequest, formatDate, getProfileImage } from "../../../core/common/api";
import AppImage from "../../../shared/AppImage";
import MetricCard from "../../../shared/MetricCard";
import PaginationBar from "../../../shared/PaginationBar";
import {
  getEnabledPermissionKeys,
  canManageAdmins,
} from "../../../core/common/adminPermissions";
import { formatAdminRole, roleTone } from "../../../shared/Utils";

const AllAdmin = () => {
  const [admins, setAdmins] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", role: "all", isActive: "all" });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalAdmins: 0,
    total: 0,
  });

  const canManage = canManageAdmins();

  const loadAdmins = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pagination.limit),
      });
      if (filters.search.trim()) params.set("search", filters.search.trim());
      if (filters.role !== "all") params.set("role", filters.role);
      if (filters.isActive !== "all") params.set("isActive", filters.isActive);

      const result = await apiRequest(`/admins?${params}`, "GET");
      setAdmins(result.admins || []);
      if (result.pagination) {
        setPagination({
          page: result.pagination.page,
          limit: result.pagination.limit,
          totalPages: result.pagination.totalPages,
          totalAdmins: result.pagination.totalAdmins || 0,
          total: result.pagination.total ?? result.pagination.totalAdmins ?? 0,
        });
      }
    } catch (error: any) {
      Swal.fire("Error", error.message || "Unable to load admins", "error");
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  useEffect(() => {
    void loadAdmins(1);
  }, [loadAdmins]);

  const metrics = useMemo(
    () => [
      { label: "Total Admins", value: pagination.totalAdmins || admins.length, icon: "🛡️", tone: "blue" },
      {
        label: "Active Admins",
        value: admins.filter((admin) => admin.isActive).length,
        icon: "✅",
        tone: "green",
      },
      {
        label: "Support Staff",
        value: admins.filter((admin) => admin.role === "support").length,
        icon: "🎧",
        tone: "orange",
      },
    ],
    [admins, pagination.totalAdmins],
  );

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">Access Control</div>
          <h1 className="page-title">
            All <em>Admins</em>
          </h1>
          <div className="page-sub">
            View and manage manager and support accounts with custom permissions.
          </div>
        </div>
        {canManage && (
          <div className="page-hdr-right">
            <Link to="/add-admins" className="btn-sm btn-primary">
              Add New Admin
            </Link>
          </div>
        )}
      </div>

      <section className="metrics-grid">
        {metrics.map((metric) => (
          <MetricCard key={metric.label} metric={metric} />
        ))}
      </section>

      <div className="toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={filters.search}
            onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
            placeholder="Search by name or email..."
          />
        </div>
        <select
          className="filter-select"
          value={filters.role}
          onChange={(event) => setFilters((current) => ({ ...current, role: event.target.value }))}
        >
          <option value="all">All Roles</option>
          <option value="superadmin">Super Admin</option>
          <option value="admin">Admin</option>
          <option value="manager">Manager</option>
          <option value="support">Support</option>
        </select>
        <select
          className="filter-select"
          value={filters.isActive}
          onChange={(event) => setFilters((current) => ({ ...current, isActive: event.target.value }))}
        >
          <option value="all">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>

      <section className="table-card">
        <div className="table-head">
          <h2>Registered Admins ({pagination.totalAdmins || admins.length})</h2>
          <p>List of all panel administrators and their access levels</p>
        </div>

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Admin</th>
                <th>Email</th>
                <th>Role</th>
                <th>Permissions</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="empty-state">Loading admins...</td></tr>
              ) : admins.length === 0 ? (
                <tr><td colSpan={7} className="empty-state">No admins found matching your filters.</td></tr>
              ) : (
                admins.map((admin) => {
                  const permissionCount = getEnabledPermissionKeys(admin.permissions || []).length;
                  const canEdit = canManage && admin.role !== "superadmin";

                  return (
                    <tr key={admin._id}>
                      <td>
                        <div className="img-name-cell">
                          <AppImage
                            src={getProfileImage(admin)}
                            alt={admin.name}
                            crossOrigin="anonymous"
                          />
                          <div>
                            <strong>{admin.name}</strong>
                            <small className="table-muted">
                              ID: #{admin._id?.slice(-6).toUpperCase()}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>{admin.email}</td>
                      <td>
                        <span className={`status-badge ${roleTone(admin.role)}`}>
                          {formatAdminRole(admin.role)}
                        </span>
                      </td>
                      <td>
                        {admin.role === "superadmin" ? (
                          <span className="status-badge warning">Full Access</span>
                        ) : (
                          <span className={`status-badge ${permissionCount > 0 ? "success" : "muted"}`}>
                            {permissionCount} enabled
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`status-badge ${admin.isActive ? "success" : "danger"}`}>
                          {admin.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>{formatDate(admin.lastLoginAt)}</td>
                      <td>
                        <div className="admin-list-actions">
                          <Link
                            to={`/admins/${admin._id}`}
                            className="btn-sm btn-outline"
                            title="View Admin"
                          >
                            View
                          </Link>
                          {canEdit && (
                            <Link
                              to={`/admins/${admin._id}?edit=1`}
                              className="btn-sm btn-primary"
                              title="Edit Admin"
                            >
                              Edit
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <PaginationBar
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total}
          totalPages={pagination.totalPages}
          itemLabel="admins"
          onPageChange={loadAdmins}
          onLimitChange={(limit) =>
            setPagination((current) => ({ ...current, page: 1, limit }))
          }
        />
      </section>
    </>
  );
};

export default AllAdmin;
